import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';

import { jwtDecode } from "jwt-decode";

import { CustomLoginService } from '@privatelib/ngx18-authz-dev';
import { BehaviorSubject, Observable, catchError, combineLatest, map, of, switchMap, tap } from 'rxjs';
import { ROUTE_CONSTANTS, API_ROUTES } from 'app/util/route.constants';
import { PreferencesService } from '../preferences/preferences.service';
import { UserManagementService } from '../user-management/user-management.service';
import { CVA_CONSTANTS, UserDetails } from 'app/util/constants';
import { SessionInactivityService } from '../session-inactivity/session-inactivity.service';
import { CVANotificationService } from '../cva-notification/cva-notification.service';
import { NotificationClassEnum } from 'app/models/notification-class.enum';
import { environment } from '../../../environments/environment';

export interface PractitionerPayload {
  providerId: string;
  firstName: string;
  lastName: string;
  email: string;
  avatar?: string;
  phoneNo?: string;
  company?: string;
  id: string;
}

export interface IRefreshTokenResponse {
  authToken: string,
  idToken: string,
  refreshToken: string
}

@Injectable({
  providedIn: 'root'
})
export class LoginService {
  tokenKey = 'idToken';
  loggedIn$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  userSubject: BehaviorSubject<PractitionerPayload | null> = new BehaviorSubject<PractitionerPayload | null>(null);
  currentUser$ = this.userSubject.asObservable();
  notificationShown = false;

  private readonly AUTH_TOKEN_KEY = 'authToken';
  private readonly REFRESH_TOKEN_KEY = 'refreshToken';
  private readonly ID_TOKEN_KEY = 'idToken';


  constructor(
    private readonly http: HttpClient,
    private readonly loginService: CustomLoginService,
    private readonly router: Router,
    private readonly preferencesService: PreferencesService,
    private readonly userManagementService: UserManagementService,
    private readonly sessionInactivityService: SessionInactivityService,
    private readonly cvaNotificationService: CVANotificationService
  ) { }

  isLoggedIn(): Observable<boolean> {
    return combineLatest([
      this.loginService.verifyIdToken().pipe(
        catchError((error) => {
          const status = error?.status ?? 0;

          // Workaround flag to avoid repeated notifications for the same error
          if (status === 423 && !this.notificationShown) {
            this.notificationShown = true;
            this.cvaNotificationService.showNotification(error.message, NotificationClassEnum.ERROR)
          }
          return of({ status });
        })
      ),
      this.loginService.verifyAuthorizationToken().pipe(catchError(() => of({ status: 0 })))
    ]).pipe(
      // eslint-disable-next-line  @typescript-eslint/no-explicit-any
      map(([idTokenResponse, authTokenResponse]: any[]) => {
        return idTokenResponse?.status === 200 && authTokenResponse?.status === 200;
      }),
      // Only fetch user details if token verification succeeded
      switchMap((tokensValid) => {
        const actionMap = {
          "true": () => this.saveUserDetails(),
          "false": () => of(false),
        };
        return actionMap[String(tokensValid)]?.();
      })
    );
  }

  logout(inDirectSignout = true) {
    this.loginService.logout().subscribe({
      next: () => {
        this.revokeOnLogout(inDirectSignout);
      },
      error: (err: Error) => {
        console.error(err)
        this.revokeOnLogout(inDirectSignout);

      }
    })
  }



  onPartialLogout() {
    this.loginService.logout(false).subscribe({
      next: () => {
        localStorage.removeItem("idToken");
        localStorage.removeItem("authToken");
        localStorage.removeItem("refreshToken");
        this.loggedIn$.next(false);
      },
      error: (err: Error) => {
        console.error(err)
      }
    })
  }

  // Decode the JWT token and extract user information
  decodeToken(token: string): PractitionerPayload {
    return jwtDecode<PractitionerPayload>(token);
  }

  /**
   * Checks the user details and refreshes if needed.
   * 
   * @param isToBeRefresh Whether the user details should be refreshed.
   * @returns Observable<boolean> Whether the user is logged in or not.
   */
  saveUserDetails(isToBeRefresh = false): Observable<boolean> {
    const token = localStorage.getItem(this.tokenKey);

    // If no token exists, return false immediately
    if (!token) {
      return of(false);
    }

    // Decode the token
    const decodedToken = this.decodeToken(token);
    if (!decodedToken) {
      return of(false);
    }

    // Check if user data should be refreshed or if it is not cached
    if (this.userSubject.value && !isToBeRefresh) {
      return of(true); // Return true if user data is already cached and no refresh is needed
    }

    // Fetch user details from API
    return this.userManagementService.getUserDetails(decodedToken.id).pipe(
      switchMap((res: { success: boolean; data: UserDetails }) => {
        if (res.success && res.data?.id) {
          this.userSubject.next({
            firstName: res.data.firstName,
            lastName: res.data.lastName,
            providerId: res.data.providerId,
            email: res.data.email,
            phoneNo: res.data?.phoneNo,
            company: res.data?.company,
            id: decodedToken.id,
          });
          return this.loadPreferences(res.data.providerId).pipe(map(() => true));
        }
        return of(false);
      }),
      catchError((error) => {
        console.log("Error on pulling user details:", error);
        return of(false);
      })
    );
  }

  private loadPreferences(providerId?: string): Observable<unknown> {
    if (!providerId) {
      return of(null);
    }

    return this.preferencesService.getPreferences(
      providerId,
      CVA_CONSTANTS.CONST.USER_PREFERENCES
    ).pipe(catchError(() => of(null)));
  }

  revokeOnLogout(inDirectSignout: boolean = false, reason?: string) {
    this.userSubject.next(null);
    this.loggedIn$.next(false);
    this.preferencesService.clearPreferences();
    localStorage.clear();
    sessionStorage.clear();
    this.sessionInactivityService.stopOnLogout();
    this.notificationShown = false;
    if (inDirectSignout) {
      if (reason) {
        this.cvaNotificationService.showNotification(reason, NotificationClassEnum.ERROR);
      }
      this.router.navigate([ROUTE_CONSTANTS.LOGIN]);
    }
  }

  /**
   * Validate OTP for two-step verification
   * @param email - User email
   * @param otp - One-time password
   * @returns Observable with OTP validation response
   */
  validateLoginOtp(email: string, otp: string): Observable<{ redirectUrl: string }> {
    return this.http.post<{ redirectUrl: string }>(`${environment.user_management_api_url}${API_ROUTES.VALIDATE_LOGIN_OTP}`, {
      email,
      otp
    });
  }

  redirectToResetPassword(redirectUrl: string) {
    redirectUrl += `&reason=expired`;
    const currentOrigin = window.location.origin;

    try {
      const url = new URL(redirectUrl);
      if (url.origin === currentOrigin) {
        // Internal route
        const relativePath = url.pathname + url.search + url.hash;
        this.navigateByUrl(relativePath);
      } else {
        // External URL
        this.goTo(redirectUrl);
      }
    } catch (err) {
      console.error('Invalid redirect URL:', err);
    }
  }

  goTo(url: string) {
    window.location.href = url;
  }

  navigateByUrl(redirectUrl: string) {
    this.router.navigateByUrl(redirectUrl);
  }


  refreshToken(): Observable<IRefreshTokenResponse> {
    const refreshToken = localStorage.getItem(this.REFRESH_TOKEN_KEY) || '';

    return this.http.post<IRefreshTokenResponse>(
      `${environment.user_management_api_url}${API_ROUTES.REFRESH_TOKEN}`,
      { refreshToken }
    ).pipe(
      tap((response: IRefreshTokenResponse) => {
        if (response?.idToken) {
          this.setIDToken(response.idToken);
        }

        if (response?.authToken) {
          this.setAuthToken(response.authToken);
        }

        if (response?.refreshToken) {
          this.setRefreshToken(response.refreshToken);
        }
      })
    );
  }

  setIDToken(token: string) {
    localStorage.setItem(this.ID_TOKEN_KEY, token);
  }

  setAuthToken(token: string): void {
    localStorage.setItem(this.AUTH_TOKEN_KEY, token);
  }

  setRefreshToken(token: string): void {
    localStorage.setItem(this.REFRESH_TOKEN_KEY, token);
  }


}
